import type { UploaderMeta, UploadSuccessResult } from '../types';
import type Uppy from '@uppy/core';
import type { Meta } from '@uppy/core';

import { useCallback, useState } from 'react';

type P<TMeta extends UploaderMeta> = {
  onClose?: () => void;
  onSuccess?: (result: UploadSuccessResult<TMeta>) => Promise<void> | void;
  uppy: Uppy<Meta, Record<string, never>>;
};

/**
 * Runs the consumer's `onSuccess` for an uploaded batch. When it succeeds,
 * `onClose` is called and the queue emptied; when it throws, the files stay
 * queued and the message is kept in `error` for inline display, so the user
 * can retry. `isConfirming` is true while an async `onSuccess` is pending.
 *
 * `reportError` puts any other error, such as a failed upload, into the same
 * `error` slot.
 */
export function useUploadConfirmation<TMeta extends UploaderMeta>({
  onClose,
  onSuccess,
  uppy,
}: P<TMeta>) {
  const [error, setError] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const reportError = useCallback((uploadError: unknown) => {
    setError(
      uploadError instanceof Error ? uploadError.message : String(uploadError),
    );
  }, []);

  const confirmUpload = useCallback(
    async (files: UploadSuccessResult<TMeta>) => {
      setError(null);
      setIsConfirming(true);

      try {
        await onSuccess?.(files);
        onClose?.();
        uppy.clear();
      } catch (confirmationError) {
        reportError(confirmationError);
      } finally {
        setIsConfirming(false);
      }
    },
    [onClose, onSuccess, reportError, uppy],
  );

  return { clearError, confirmUpload, error, isConfirming, reportError };
}
